// Cookie consent and advertising measurement: where analytics and the Meta Pixel may load, and the
// visitor's choices. Analytics (GA4, PostHog) are loaded by public/index.html, which reads the same
// analytics choice and keeps its own copy of CONSENT_COUNTRIES: change both together.
//
// The rules, from the Privacy Policy section 4.4 (Ryan, 2026-10-11: "we def need to be allowed to use
// pixel ... change the PP and anything else"):
//   - Never on pages the Client and Contractor Privacy Notices cover, or in the portal or admin. Those
//     notices promise no advertising trackers on client-facing pages and no advertising use of client or
//     contractor data, and they stay true by keeping the pixel off these routes (AD_FREE_ROUTE).
//   - EEA, UK and Switzerland: only after the visitor chooses "Accept" (consent).
//   - Elsewhere: on unless the visitor opts out on /privacy-choices, or the browser sends Global Privacy
//     Control, which counts as an opt-out on its own.
//   - Country unknown (the geo lookup failed, or local dev): off. It fails closed.
// Choices live in this browser only (localStorage, with a cookie copy), so they apply per browser.

const OPT_OUT_KEY = 'pxly_ad_optout'; // '1' when opted out
const CONSENT_KEY = 'pxly_ad_consent'; // 'granted' | 'denied', consent regions only
const ANALYTICS_KEY = 'pxly_analytics_consent'; // 'granted' | 'denied', consent regions only (read by index.html)
// The ad that brought a visitor to Pixally Prep's pages, kept for 90 days only while the pixel may run
// (lib/prepAttribution.js). public/index.html's attribution script uses the same name.
export const PREP_SOURCE_KEY = 'pxly_prep_source';
export const AD_CHOICE_EVENT = 'pxly-ad-choice';

// The pixel ID is public by design (it is in every page that loads the pixel). Unset, the pixel never
// loads and the cookie notice does not mention advertising.
export const META_PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || '').trim();

const EEA = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT',
  'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO',
];
export const CONSENT_COUNTRIES = new Set([...EEA, 'GB', 'CH']);

// Pages where the pixel never loads, whatever the visitor chose.
const AD_FREE_ROUTE =
  /^\/(portal|admin|client-terms|client-privacy|contractor-terms|contractor-privacy|rules|official-rules|first-look|claim|privacy-choices)(\/|$)|^\/preview\/product(\/|$)/;
export const isAdFreeRoute = (pathname) => AD_FREE_ROUTE.test(pathname || '/');

export const gpcEnabled = () => {
  try {
    return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
  } catch (_) {
    return false;
  }
};

const read = (key) => {
  try {
    const v = window.localStorage.getItem(key);
    if (v !== null) return v;
  } catch (_) {
    /* storage blocked: fall back to the cookie */
  }
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (_) {
    /* storage blocked: the cookie below still records it */
  }
  try {
    const maxAge = value === null ? 0 : 60 * 60 * 24 * 395; // about 13 months
    document.cookie = `${key}=${encodeURIComponent(value || '')}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
  } catch (_) {
    /* nothing more we can do */
  }
  try {
    window.dispatchEvent(new Event(AD_CHOICE_EVENT));
  } catch (_) {
    /* old browsers */
  }
};

export const readAdChoices = () => ({
  optedOut: read(OPT_OUT_KEY) === '1',
  consent: read(CONSENT_KEY), // 'granted' | 'denied' | null
  analytics: read(ANALYTICS_KEY), // 'granted' | 'denied' | null
});

export const setOptedOut = (optedOut) => write(OPT_OUT_KEY, optedOut ? '1' : null);
export const setConsent = (granted) => write(CONSENT_KEY, granted ? 'granted' : 'denied');
export const setAnalyticsConsent = (granted) => write(ANALYTICS_KEY, granted ? 'granted' : 'denied');

export const inConsentRegion = (geo) => geo.status === 'ready' && CONSENT_COUNTRIES.has(geo.country);

// Cookies the analytics and advertising scripts set on this site. Deleted when consent is withdrawn.
const TRACKING_COOKIE = /^(_ga|_gid|_gat|ph_|_fbp|_fbc)/;
const clearTrackingStorage = () => {
  try {
    document.cookie.split('; ').forEach((pair) => {
      const name = pair.split('=')[0];
      if (!TRACKING_COOKIE.test(name)) return;
      // Expiring, not setting: GA writes _ga on the registrable domain, so it has to be expired there too.
      const host = window.location.hostname;
      const parent = host.split('.').slice(-2).join('.');
      [`; Path=/`, `; Path=/; Domain=${host}`, `; Path=/; Domain=${parent}`].forEach((scope) => {
        document.cookie = `${name}=; Max-Age=0${scope}`;
      });
    });
  } catch (_) {
    /* nothing more we can do */
  }
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith('ph_') || k.startsWith('_fb') || k === PREP_SOURCE_KEY)
      .forEach((k) => window.localStorage.removeItem(k));
  } catch (_) {
    /* storage blocked */
  }
};

/**
 * Record a consent-region answer (the cookie notice, or /privacy-choices). `ads` is ignored while no
 * pixel is configured, so an "Accept" given before the pixel existed never counts as consent to it.
 * Withdrawing something that already ran in this visit deletes its cookies and reloads the page, which
 * is the only sure way to stop scripts that are already running.
 */
export const applyConsent = ({ analytics, ads }) => {
  const before = readAdChoices();
  setAnalyticsConsent(analytics);
  if (META_PIXEL_ID) setConsent(ads);
  const analyticsRan = typeof window !== 'undefined' && window.pxlyAnalytics && window.pxlyAnalytics.isLoaded();
  const pixelRan = typeof window !== 'undefined' && typeof window.fbq === 'function';
  const withdrewAnalytics = before.analytics === 'granted' && !analytics && analyticsRan;
  const withdrewAds = before.consent === 'granted' && !ads && META_PIXEL_ID && pixelRan;
  if (withdrewAnalytics || withdrewAds) {
    clearTrackingStorage();
    window.location.reload();
    return;
  }
  if (analytics && window.pxlyAnalytics) window.pxlyAnalytics.load();
};

/**
 * May the pixel run on this page for this visitor?
 * `geo` is useVisitorCountry()'s { country, status }.
 */
export const pixelAllowed = ({ pixelId, pathname, geo, choices }) => {
  if (!pixelId) return false;
  if (isAdFreeRoute(pathname)) return false;
  if (geo.status !== 'ready' || !geo.country) return false;
  if (gpcEnabled() || choices.optedOut) return false;
  if (CONSENT_COUNTRIES.has(geo.country)) return choices.consent === 'granted';
  return true;
};

/**
 * Has advertising measurement been turned off for this visitor, by their own signal (Global Privacy
 * Control, the opt-out on /privacy-choices, "Decline") or because they are in a consent region and
 * have not said yes? Not the same as !pixelAllowed: an ad-free page, or a country not yet known, is
 * "not now", not "off". Pixally Prep's stored ad source is deleted when this is true
 * (lib/prepAttribution.js).
 */
export const adsDenied = ({ geo, choices }) =>
  gpcEnabled() ||
  choices.optedOut ||
  choices.consent === 'denied' ||
  (inConsentRegion(geo) && choices.consent !== 'granted');

// Whether the pixel is live on the current page, set by MetaPixel. Events fired from elsewhere (the
// signup Lead in lib/analytics.js) check this rather than relying on Meta's consent revoke to hold them.
let pixelActive = false;
export const setPixelActive = (active) => {
  pixelActive = Boolean(active);
};
export const isPixelActive = () => pixelActive;

/** Should the cookie notice show? Consent regions only, until the visitor has answered everything it asks. */
export const cookieNoticeNeeded = ({ pathname, geo, choices }) =>
  inConsentRegion(geo) &&
  pathname !== '/privacy-choices' &&
  (!choices.analytics || (Boolean(META_PIXEL_ID) && !choices.consent));
